import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { Box, Button, Chip, CircularProgress, TableCell, TableRow, Typography } from '@mui/material';
import ValoresCorrigidosModal from './ValoresCorrigidosModal';

interface InvestidorCorrigidoItemProps {
    investidor: any;
    obraId: number;
    dataTerminoPrevista: string;
}

const formatCurrency = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const InvestidorCorrigidoItem: React.FC<InvestidorCorrigidoItemProps> = ({ investidor, obraId, dataTerminoPrevista }) => {
    const [valoresCorrigidos, setValoresCorrigidos] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const calculateValoresCorrigidos = async () => {
            setIsLoading(true);
            const { data, error } = await supabase.rpc('calculate_corrected_value', {
                p_obra_id: obraId,
                p_investidor_id: investidor.id,
                p_data_limite: dataTerminoPrevista,
            });

            if (error) {
                console.error('Erro ao calcular valores corrigidos:', error);
            } else {
                setValoresCorrigidos(data);
            }
            setIsLoading(false);
        };

        calculateValoresCorrigidos();
    }, [investidor.id, obraId, dataTerminoPrevista]);

    const round2 = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;
    const totalAportes = round2(investidor.total_aportes || 0);
    const totalResgates = round2(investidor.total_resgates || 0);
    const totalInvestido = round2(investidor.total || 0);
    const saldoLiquido = round2(totalAportes - totalResgates);
    const saldoFinal = round2(saldoLiquido - totalInvestido);

    return (
        <TableRow>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: investidor.is_responsavel ? 700 : 400 }}>
                        {investidor.nome}
                    </Typography>
                    {investidor.is_responsavel && (
                        <Chip
                            label={
                                investidor.percentual_participacao != null
                                    ? `Responsável (${Number(investidor.percentual_participacao).toFixed(0)}%)`
                                    : 'Responsável'
                            }
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                        />
                    )}
                </Box>
            </TableCell>
            <TableCell align="right">{formatCurrency(saldoLiquido)}</TableCell>
            <TableCell align="right">{formatCurrency(totalInvestido)}</TableCell>
            <TableCell align="right" sx={{ color: saldoFinal >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                {formatCurrency(saldoFinal)}
            </TableCell>
            <TableCell align="right">
                {isLoading ? (
                    <CircularProgress size={20} />
                ) : (
                    <>
                        {valoresCorrigidos && formatCurrency(valoresCorrigidos.valorCorrigido)}
                    </>
                )}
            </TableCell>
            <TableCell align="right">
                {valoresCorrigidos && formatCurrency(valoresCorrigidos.valorCorrigido - (investidor.total || 0))}
            </TableCell>
            <TableCell align="right">
                {(() => {
                    if (!valoresCorrigidos || !valoresCorrigidos.detalhamento || valoresCorrigidos.detalhamento.length === 0) {
                        return '-';
                    }
                    // Find the last month with non-zero interest
                    const lastInterest = [...valoresCorrigidos.detalhamento].reverse().find((d: any) => d.jurosRs > 0);
                    return lastInterest ? formatCurrency(lastInterest.jurosRs) : formatCurrency(0);
                })()}
            </TableCell>
            <TableCell align="right">
                {valoresCorrigidos && (
                    <>
                        <Button size="small" onClick={() => setIsModalOpen(true)}>
                            Detalhes
                        </Button>
                        <ValoresCorrigidosModal
                            open={isModalOpen}
                            onClose={() => setIsModalOpen(false)}
                            detalhamento={valoresCorrigidos.detalhamento}
                            valorCorrigido={valoresCorrigidos.valorCorrigido}
                        />
                    </>
                )}
            </TableCell>
        </TableRow>
    );
};

export default InvestidorCorrigidoItem;
